import { Control } from '@velocity-exchange/icons';
import { memo, useState } from 'react';
import Button from 'src/components/primitives/Button/Button';
import BottomDrawer from 'src/components/Drawers/BaseDrawers/BottomDrawer/BottomDrawer';
import useCloseBottomDrawer from 'src/components/Drawers/BaseDrawers/BottomDrawer/hooks/useCloseBottomDrawer';
import ControlledSlippageToleranceInput from 'src/components/TradeForm/SlippageToleranceInput/ControlledSlippageToleranceInput';
import useSlippageToleranceInput from 'src/components/TradeForm/SlippageToleranceInput/hooks/useSlippageToleranceInput';
import ValueDisplay from 'src/components/ValueDisplay';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';

function BottomDrawerSlippageToleranceInput() {
	const [isOpen, setIsOpen] = useState(false);
	const side = useDriftStore((s) => s.tradeForm.side);

	const { closeDrawer, isDrawerClosing } = useCloseBottomDrawer({
		onClose: () => setIsOpen(false),
	});

	const {
		currentValue,
		isCurrentDynamic,
		customInputValue,
		handleCustomInputChange,
		handlePresetClick,
		presetOptions,
		isCustomInputFocused,
		handleBlur,
		handleFocus,
		auctionStartPrice,
		auctionEndPrice,
		dynamicSlippageString,
	} = useSlippageToleranceInput();

	const buttonEndContent = (() => {
		if (isCurrentDynamic) {
			return dynamicSlippageString ? `${dynamicSlippageString}%` : '';
		}

		return `${currentValue}%`;
	})();

	return (
		<>
			<button
				onClick={() => setIsOpen(true)}
				className="flex w-full items-center justify-between typo-body-xs text-text-secondary"
			>
				Slippage {isCurrentDynamic ? '(Dynamic)' : ''}
				<div className="flex items-center gap-x-xs">
					<span className="text-text-default">{buttonEndContent}</span>
					<Control size={16} />
				</div>
			</button>
			{isOpen && (
				<BottomDrawer.Container
					closeDrawer={closeDrawer}
					isDrawerClosing={isDrawerClosing}
				>
					<BottomDrawer.Title closeDrawer={closeDrawer}>
						Slippage Tolerance
					</BottomDrawer.Title>
					<BottomDrawer.Body>
						<ControlledSlippageToleranceInput
							handleCustomInputChange={handleCustomInputChange}
							handlePresetClick={handlePresetClick}
							presetOptions={presetOptions}
							customInputValue={customInputValue}
							isCustomInputFocused={isCustomInputFocused}
							handleBlur={handleBlur}
							handleFocus={handleFocus}
							currentValue={currentValue}
							isCurrentDynamic={isCurrentDynamic}
						/>
						<div className="flex flex-col gap-y-sm">
							<ValueDisplay.Default
								label="Slippage Tolerance"
								value={
									dynamicSlippageString
										? `${dynamicSlippageString}%`
										: // If there's no `dynamicSlippageString` but `currentValue` is `"dynamic"`,
											// that means the user hasn't entered a size yet, so we just show "Dynamic"
											// as a placeholder. In other parts of the app, we don't show anything.
											currentValue === 'dynamic'
											? 'Dynamic'
											: `${currentValue}%`
								}
							/>
							<ValueDisplay.Default
								label="Auction Start Price"
								value={auctionStartPrice}
							/>
							<ValueDisplay.Default
								label={`${side === 'buy' ? 'Max' : 'Min'} Price`}
								value={auctionEndPrice}
							/>
						</div>
					</BottomDrawer.Body>
					<BottomDrawer.Footer>
						<Button
							className="w-full"
							size="lg"
							variant="secondary"
							onClick={closeDrawer}
						>
							Close
						</Button>
					</BottomDrawer.Footer>
				</BottomDrawer.Container>
			)}
		</>
	);
}

export default memo(BottomDrawerSlippageToleranceInput);
